import { PermissionCode } from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { ListPagination } from '@/components/list-pagination';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import { KbStatusBadge, KbVisibilityBadge } from '@/products/helpdesk/features/kb/kb-badges';
import {
  useCreateKbCategory,
  useKbArticles,
  useKbLookups,
  useKbSearch,
} from '@/products/helpdesk/features/kb/use-kb';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { formatDateTime } from '@/lib/format';

const NONE = 'all';

export function KbPage() {
  usePageTitle('Wissen');
  const me = useMe();
  const canWrite = hasPermission(me.data, PermissionCode.KB_WRITE);
  const lookups = useKbLookups();
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  const [categoryId, setCategoryId] = useState(NONE);
  const [status, setStatus] = useState(NONE);
  const [page, setPage] = useState(1);
  const list = useKbArticles({
    page,
    pageSize: 25,
    sortBy: 'updatedAt',
    sortOrder: 'desc',
    categoryId: categoryId === NONE ? undefined : categoryId,
    status:
      status === NONE ? undefined : (status as 'DRAFT' | 'PUBLISHED' | 'IN_REVIEW' | 'ARCHIVED'),
  });
  const search = useKbSearch(q.trim() ? { q: q.trim(), limit: 8 } : null);
  const createCategory = useCreateKbCategory();
  const [newCategory, setNewCategory] = useState('');

  useEffect(() => {
    const handle = window.setTimeout(() => setQ(searchInput.trim()), 400);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  async function onCreateCategory() {
    const name = newCategory.trim();
    if (!name) {
      return;
    }
    try {
      await createCategory.mutateAsync({ name });
      setNewCategory('');
    } catch (error) {
      window.alert(
        error instanceof ApiRequestError
          ? error.message
          : 'Kategorie konnte nicht angelegt werden.',
      );
    }
  }

  return (
    <section className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Wissensdatenbank</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Artikel durchsuchen oder nach Kategorie filtern.
            {lookups.data && !lookups.data.embedding.available
              ? ' Semantische Suche ist gerade nicht verfügbar — es wird nur die Textsuche verwendet.'
              : null}
          </p>
        </div>
        {canWrite && (
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline">
              <Link to="/helpdesk/wissen/assistent">KB-Assistent</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/helpdesk/wissen/optimierung">KB-Optimierung</Link>
            </Button>
            <Button asChild>
              <Link to="/helpdesk/wissen/neu">Neuer Artikel</Link>
            </Button>
          </div>
        )}
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <div className="space-y-1.5 md:col-span-2">
          <Label htmlFor="kb-search">Suche</Label>
          <Input
            id="kb-search"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="z. B. VPN, Passwort, Drucker …"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Kategorie</Label>
          <Select
            value={categoryId}
            onValueChange={(value) => {
              setCategoryId(value);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Alle" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Alle Kategorien</SelectItem>
              {(lookups.data?.categories ?? []).map((category) => (
                <SelectItem key={category.id} value={category.id}>
                  {category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {q && (
        <Card>
          <CardHeader>
            <CardTitle>Treffer</CardTitle>
            <CardDescription>
              {search.data
                ? `${search.data.items.length} Ergebnisse · Modus: ${
                    search.data.mode === 'hybrid'
                      ? 'Hybrid'
                      : search.data.mode === 'semantic'
                        ? 'Semantisch'
                        : 'Volltext'
                  }`
                : 'Suche läuft …'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {search.isPending && (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2Icon className="size-4 animate-spin" aria-hidden />
                Suche …
              </p>
            )}
            {search.data?.items.length === 0 && (
              <p className="text-sm text-muted-foreground">Keine Treffer.</p>
            )}
            {search.data?.items.map((hit) => (
              <Link
                key={hit.article.id}
                to={`/helpdesk/wissen/${hit.article.slug}`}
                className="block rounded-md border p-3 hover:bg-muted/40"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{hit.article.title}</span>
                  <KbStatusBadge status={hit.article.status} />
                  <KbVisibilityBadge visibility={hit.article.visibility} />
                </div>
                {hit.snippet && <p className="mt-1 text-sm text-muted-foreground">{hit.snippet}</p>}
              </Link>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="flex flex-wrap items-end gap-3">
        {canWrite && (
          <>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select
                value={status}
                onValueChange={(value) => {
                  setStatus(value);
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Alle Status</SelectItem>
                  <SelectItem value="PUBLISHED">Veröffentlicht</SelectItem>
                  <SelectItem value="DRAFT">Entwurf</SelectItem>
                  <SelectItem value="IN_REVIEW">In Prüfung</SelectItem>
                  <SelectItem value="ARCHIVED">Archiviert</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <div className="space-y-1.5">
                <Label htmlFor="new-cat">Neue Kategorie</Label>
                <Input
                  id="new-cat"
                  value={newCategory}
                  onChange={(event) => setNewCategory(event.target.value)}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => void onCreateCategory()}
                disabled={createCategory.isPending}
              >
                Anlegen
              </Button>
            </div>
          </>
        )}
      </div>

      {list.isPending && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Artikel werden geladen …
        </p>
      )}
      {list.data && (
        <ul className="divide-y rounded-md border">
          {list.data.items.map((article) => (
            <li key={article.id}>
              <Link
                to={`/helpdesk/wissen/${article.slug}`}
                className="flex flex-col gap-1 px-4 py-3 hover:bg-muted/40 sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <p className="font-medium">{article.title}</p>
                  <p className="text-sm text-muted-foreground">
                    {article.category?.name ?? 'Ohne Kategorie'}
                    {article.tags.length > 0
                      ? ` · ${article.tags.map((tag) => tag.name).join(', ')}`
                      : ''}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <KbStatusBadge status={article.status} />
                  <KbVisibilityBadge visibility={article.visibility} />
                  <span className="text-xs text-muted-foreground">
                    {formatDateTime(article.updatedAt)}
                  </span>
                </div>
              </Link>
            </li>
          ))}
          {list.data.items.length === 0 && (
            <li className="px-4 py-6 text-sm text-muted-foreground">
              Noch keine Artikel in dieser Auswahl.
            </li>
          )}
        </ul>
      )}
      {list.data ? (
        <ListPagination
          page={list.data.page}
          pageSize={list.data.pageSize}
          total={list.data.total}
          onPageChange={setPage}
        />
      ) : null}
    </section>
  );
}
